/**
 * ModuleFlyout - Flyout-meny for modulnavigasjon
 * Viser alle tilgjengelige moduler med status og navigasjon
 * @version 1.0.0
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutGrid, Scale, Shield, Briefcase, Home, X,
  ChevronRight, Sparkles, Lock, Building2, ScrollText, Heart
} from 'lucide-react';

// Modul-konfigurasjon
const MODULES = [
  {
    id: 'arbeidsrett-privat',
    name: 'Arbeidsrett – Privat',
    description: 'Dine rettigheter som arbeidstaker',
    icon: Briefcase,
    path: '/arbeidsrett-privat',
    status: 'active',
    badge: null,
    color: 'copper'
  },
  {
    id: 'forbrukerrett',
    name: 'Forbrukerrett',
    description: 'Reklamasjon og klageveiviser',
    icon: Scale,
    path: '/forbrukerrett',
    status: 'active',
    color: 'copper'
  },
  {
    id: 'barnevern',
    name: 'Barnevern',
    description: 'AI-assistert veiledning',
    icon: Shield,
    path: '/barnevern',
    status: 'active',
    color: 'blue'
  },
  {
    id: 'husleierett',
    name: 'Husleierett',
    description: 'Leieforhold og depositum',
    icon: Home,
    path: '/husleierett',
    status: 'active',
    badge: 'Master',
    color: 'copper'
  },
  {
    id: 'forvaltningsrett',
    name: 'Forvaltningsrett',
    description: 'Dine rettigheter i møte med staten',
    icon: Building2,
    path: '/forvaltningsrett',
    status: 'active',
    badge: 'Ny',
    color: 'copper'
  },
  {
    id: 'eiendomsrett',
    name: 'Eiendomsrett',
    description: 'Bolig, naboforhold og husleie',
    icon: Home,
    path: '/eiendomsrett',
    status: 'active',
    badge: 'Ny',
    color: 'copper'
  },
  {
    id: 'arverett',
    name: 'Arverett',
    description: 'Sikring av fremtiden',
    icon: ScrollText,
    path: '/arverett',
    status: 'active',
    badge: 'Ny',
    color: 'copper'
  },
  {
    id: 'trygderett',
    name: 'Trygderett',
    description: 'NAV og sosiale rettigheter',
    icon: Heart,
    path: '/trygderett',
    status: 'active',
    badge: 'Ny',
    color: 'copper'
  }
];

const statusConfig = {
  active: {
    badge: null,
    badgeClass: '',
    disabled: false
  },
  coming_soon: {
    badge: 'Kommer snart',
    badgeClass: 'bg-[#1B2740] text-[#8899A8]',
    disabled: true
  },
  beta: {
    badge: 'Beta',
    badgeClass: 'bg-blue-500/20 text-blue-400',
    disabled: false
  }
};

export function ModuleFlyout({ isOpen, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const panelRef = useRef(null);

  // Lukk ved klikk utenfor
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (panelRef.current && !panelRef.current.contains(event.target)) {
        // Sjekk om klikket var på "Moduler"-knappen (den skal håndteres separat)
        const modulerButton = document.querySelector('[data-testid="modules-flyout-trigger"]');
        if (modulerButton && modulerButton.contains(event.target)) {
          return;
        }
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Lukk med Escape-tast
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleModuleClick = (module) => {
    if (module.status === 'coming_soon') return;
    navigate(module.path);
    onClose();
  };

  const isCurrentModule = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <div
        className={`fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300 md:hidden ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={onClose}
      />

      {/* Flyout Panel */}
      <div
        ref={panelRef}
        data-testid="modules-flyout-panel"
        className={`
          fixed z-50
          bg-[#131C2E]/98 backdrop-blur-xl
          border border-white/10 shadow-2xl
          will-change-transform
          
          /* Desktop: til høyre for sidebar (288px = sidebar width) */
          md:left-[288px] md:top-0 md:bottom-auto md:w-72 md:rounded-r-lg md:rounded-l-none
          
          /* Mobile: bunn-meny */
          left-0 right-0 bottom-0 rounded-t-2xl md:right-auto md:bottom-auto
          
          /* GPU-akselerert animasjon */
          transition-[transform,opacity] duration-300 ease-out
          ${isOpen 
            ? 'translate-y-0 md:translate-x-0 opacity-100' 
            : 'translate-y-full md:translate-y-0 md:-translate-x-4 opacity-0 pointer-events-none'
          }
        `}
        style={{
          maxHeight: 'calc(100vh - 100px)'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <div className="flex items-center gap-2">
            <LayoutGrid className="w-5 h-5 text-[#C07850]" />
            <h2 className="text-sm font-bold text-[#F0F4F8]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Moduler
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-[#8899A8] hover:text-[#F0F4F8] transition-colors"
            data-testid="close-modules-flyout"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Module List */}
        <div className="p-2 max-h-[60vh] md:max-h-none overflow-y-auto">
          {MODULES.map((module) => {
            const Icon = module.icon;
            const config = statusConfig[module.status];
            const isActive = isCurrentModule(module.path);
            const isDisabled = config.disabled;

            return (
              <button
                key={module.id}
                onClick={() => handleModuleClick(module)}
                disabled={isDisabled}
                data-testid={`module-${module.id}`}
                className={`
                  w-full flex items-center gap-3 p-3 rounded-lg mb-1 text-left
                  transition-all duration-200 group
                  ${isActive 
                    ? 'bg-[#C07850]/20 border border-[#C07850]/30' 
                    : isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'hover:bg-white/5 border border-transparent'
                  }
                `}
              >
                {/* Icon */}
                <div className={`
                  w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0
                  ${isActive 
                    ? 'bg-[#C07850]/30' 
                    : isDisabled 
                      ? 'bg-[#1B2740]/50'
                      : 'bg-[#1B2740] group-hover:bg-[#1B2740]/80'
                  }
                `}>
                  {isDisabled ? (
                    <Lock className="w-5 h-5 text-[#8899A8]" />
                  ) : (
                    <Icon className={`w-5 h-5 ${isActive ? 'text-[#C07850]' : 'text-[#8899A8] group-hover:text-[#F0F4F8]'}`} />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium truncate ${
                      isActive ? 'text-[#F0F4F8]' : isDisabled ? 'text-[#8899A8]' : 'text-[#B8C8D8] group-hover:text-[#F0F4F8]'
                    }`}>
                      {module.name}
                    </span>
                    
                    {/* Master Badge */}
                    {module.badge && !isDisabled && (
                      <span className="px-1.5 py-0.5 bg-[#C07850]/20 text-[#C07850] text-[10px] font-bold rounded flex items-center gap-0.5">
                        <Sparkles className="w-2.5 h-2.5" />
                        {module.badge}
                      </span>
                    )}
                    
                    {/* Status Badge */}
                    {config.badge && (
                      <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${config.badgeClass}`}>
                        {config.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#8899A8] truncate mt-0.5">
                    {module.description}
                  </p>
                </div>

                {/* Arrow */}
                {!isDisabled && (
                  <ChevronRight className={`w-4 h-4 flex-shrink-0 transition-transform ${
                    isActive ? 'text-[#C07850]' : 'text-[#8899A8] group-hover:translate-x-0.5'
                  }`} />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-white/5">
          <p className="text-[10px] text-[#8899A8] text-center">
            Flere moduler kommer snart
          </p>
        </div>
      </div>
    </>
  );
}

// Trigger-knapp for å åpne flyout
export function ModuleTriggerButton({ onClick, isOpen }) {
  return (
    <button
      onClick={onClick}
      data-testid="modules-flyout-trigger"
      className={`
        flex items-center gap-2 px-3 py-2 rounded-lg w-full
        text-sm font-medium transition-all duration-200
        ${isOpen 
          ? 'bg-[#C07850]/20 text-[#C07850] border border-[#C07850]/30' 
          : 'text-[#8899A8] hover:text-[#F0F4F8] hover:bg-[#1B2740]/50 border border-transparent'
        }
      `}
    >
      <LayoutGrid className="w-4 h-4" />
      <span className="btn-text">Moduler</span>
      <ChevronRight className={`w-4 h-4 ml-auto transition-transform ${isOpen ? 'rotate-90' : ''}`} />
    </button>
  );
}

export default ModuleFlyout;
